<template>
  <Competitors
    v-if="useCompetitors"
    :eventId="eventId"
    :class="[classes.competitors, 'competitors']"
  />
  <LinkWrapper v-else :href="eventLink" target="_blank" @click="handleClick">
    <div :class="classes.eventWrapper">
      <FontFillText
        :text="eventName"
        styleGroup="Event Name"
        :getFont="fixtureNameStyle.get"
        :setFont="fixtureNameStyle.set"
        :minSize="eventTextSizes.min"
        :maxSize="eventTextSizes.max"
        :lineHeight="1.2"
        alignment="center"
        justifyContent="center"
      ></FontFillText>
    </div>
  </LinkWrapper>
</template>

<script lang="ts">
import { defineComponent, computed, ComputedRef } from "vue";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import Competitors from "./CompetitorsVertical.vue";
import LinkWrapper from "./LinkWrapper.vue";
import FontFillText from "./FontFillText.vue";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { Brand, TemplateSize } from "@fresh8/types/src";
import {
  injectTemplateSize,
  SKYSCRAPER
} from "@fresh8/ad-data-composition/src/useTemplateSize";

import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { injectCarouselContext } from "../lib/provideInjectors";
import { SportEvent } from "@fresh8/types/src/betData";

const { useStyles } = createUseStyles({
  eventName: {
    fontSize: "17px",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center"
  },
  eventWrapper: {
    width: "inherit",
    height: "40px",
    minHeight: "40px",
    maxHeight: "134px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px"
  },
  competitors: {
    display: "flex",
    alignItems: "start",
    justifyContent: "center",
    borderBottom: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    "& > .versus": {
      fontSize: "11px",
      margin: "0 5px",
      maxWidth: "10px",
      textAlign: "center"
    },
    "& > .team-name": {
      fontSize: "13px",
      textAlign: "center"
    }
  }
});

export default defineComponent({
  name: "EventName",
  components: {
    Competitors,
    LinkWrapper,
    FontFillText
  },
  props: {
    eventId: { type: String, required: true },
    forceEventName: { type: Boolean, default: false }
  },
  setup(props) {
    const { adTemplateBranding } = injectBrand();
    const carouselContext = injectCarouselContext();

    const reactiveStyles = computed(() => ({
      ...adTemplateBranding,
      isCarousel: carouselContext.value.isCarousel
    }));

    const { classesRef } = useStyles(reactiveStyles);

    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { eventId: props.eventId, cardNumber: cardNumber },
      "Event Name"
    );
    const layout = injectTemplateSize() as { size: ComputedRef<TemplateSize> };

    const useCompetitors = computed(() => {
      return (
        !props.forceEventName &&
        event.value.competitors.home.name.full?.length > 0 &&
        event.value.competitors.away.name.full?.length > 0
      );
    });

    const eventTextSizes = computed(() => ({
      min: layout.size?.value.name === SKYSCRAPER.name ? 10 : 14,
      max: layout.size?.value.name === SKYSCRAPER.name ? 14 : 17
    }));

    return {
      classes: classesRef,
      eventTextSizes,
      layoutClass: computed(() => layout.size.value.name),
      useCompetitors,
      eventLink: computed(() => event.value.eventUrl),
      eventName: computed(() => event.value.name),
      handleClick,
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand && brand.adTemplate.market.teamNameFont,
        set: (brand: Brand | undefined, value: string) =>
          brand && (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
